/**
 * ``usePanelVisible`` — is this dockview panel the active tab of its group?
 *
 * # Why mountedness is not visibility
 *
 * Dockview keeps a non-active tab's component **mounted**, so every hook in a
 * backgrounded panel keeps running. That is usually what you want (state
 * survives tab switching), and it is a trap for anything that costs something
 * per render or per interval.
 *
 * It bit REQ-STATUS-099 directly. ``system-status`` ships in the **default
 * layout** (:file:`./presets`) in a tab group whose ``activeView`` is ``map``,
 * so the System panel is mounted in essentially every session — including every
 * session where the operator never opens it. A freshness refresh gated on "the
 * panel's hooks are running" therefore polls in every authenticated tab, which
 * is the opposite of the scoping that justified doing it in the UI rather than
 * widening the SSE frame. Measured live before this hook existed: four
 * ``/api/system/snapshot`` reads in 25 s with the panel never opened.
 *
 * # Use it for cost, not for correctness
 *
 * Gate *work* on this (fetches, timers, animation). Do not gate *state* on it —
 * a panel that discards state when backgrounded loses it on a tab switch, which
 * is the behaviour dockview's keep-mounted exists to avoid.
 *
 * Returns ``true`` when no api is supplied, so a panel rendered outside a
 * dockview host (tests, storybook) behaves as visible rather than as
 * permanently hidden — the fail-safe direction for a freshness gate, since the
 * cost of being wrong is a request rather than a frozen display.
 *
 * Cross-references:
 *   - REQ-STATUS-099 (what this gates); ADR-024 §2 (the panel layout).
 *   - :func:`useKeepSelectedRowInView` — the other consumer of
 *     ``PanelVisibilityApi``, and where that type is declared.
 */
import { useCallback, useSyncExternalStore } from "react";
import type { PanelVisibilityApi } from "@/selection/use-keep-selected-row-in-view";

/**
 * Track a dockview panel's tab-visibility reactively.
 *
 * @param panelApi - the panel's dockview api, or ``undefined`` when not hosted
 *   in dockview (in which case the panel is treated as visible).
 * @returns whether the panel is currently the active tab of its group.
 */
export function usePanelVisible(panelApi?: PanelVisibilityApi): boolean {
  // `useSyncExternalStore` rather than state-plus-effect: dockview's api IS an
  // external store, and mirroring it into state means a synchronous `setState`
  // in an effect to close the subscribe-time gap (visibility can change between
  // first render and the effect, and the listener reports only transitions).
  // That is the cascading-render pattern `react-hooks` rejects. Reading the
  // snapshot directly has no gap to close. Same shape as
  // :func:`useSystemStatusLiveFrame`.
  const subscribe = useCallback(
    (onStoreChange: () => void) => {
      if (panelApi === undefined) return () => undefined;
      const disposable = panelApi.onDidVisibilityChange(() => {
        onStoreChange();
      });
      return () => disposable.dispose();
    },
    [panelApi],
  );

  return useSyncExternalStore(subscribe, () => panelApi?.isVisible ?? true);
}
